import {Injectable, RendererFactory2} from '@angular/core';
import {MatSnackBar, MatSnackBarRef} from '@angular/material/snack-bar';
import {SelectionToolbarComponent} from '@app/selection/selection-toolbar/selection-toolbar.component';

import {ReplaySubject, Subject} from 'rxjs';

import {Asset, AssetService} from '../asset';
import {Rect} from '../shared/euclid';
import {StateService} from '../state/state.service';

// FIXME: This is a hardcoded value from the mat-toolbar component
const kToolbarHeight = 96;
const kSidebarWidth = 48;

export enum SelectionMode {
    Add,
    Replace,
    Clear,
    Remove,
}

class AssetRect {
    rect: Rect;
    assetId: string;
}

@Injectable({
    providedIn: 'root',
})
export class SelectionService {
    public selectionMask: Subject<Rect>;
    public selection: ReplaySubject<string[]>;
    public selectionMode: ReplaySubject<boolean>;

    private assetIds: string[];
    private assetRects: AssetRect[][];
    private selectedAssetIds: string[];
    private snackBarRef: MatSnackBarRef<SelectionToolbarComponent>;
    private shiftDown: boolean;
    private ctrlDown: boolean;

    constructor(
        private assetService: AssetService,
        private stateService: StateService,
        private snackBar: MatSnackBar,
        private renderFactory: RendererFactory2,
    ) {
        this.selectionMask = new Subject<Rect>();
        this.selection = new ReplaySubject<string[]>();
        this.selectionMode = new ReplaySubject<boolean>(1);

        this.selectedAssetIds = [];
        this.assetIds = [];
        this.assetRects = [];
        this.snackBarRef = null;
        this.shiftDown = false;
        this.ctrlDown = false;

        this.assetService.filteredAssets.subscribe((assets) => {
            this.assetIds = assets.map((asset) => asset.id);
            this.assetRects = [];
            this.generateRects();
        });

        const renderer = this.renderFactory.createRenderer(null, null);
        renderer.listen('window', 'resize', () => {
            this.generateRects();
        });
        renderer.listen('window', 'keydown', (event) => {
            this.ctrlDown = event.ctrlKey;
            this.shiftDown = event.shiftKey;
        });
        renderer.listen('window', 'keyup', (event) => {
            this.ctrlDown = event.ctrlKey;
            this.shiftDown = event.shiftKey;
        });

        this.generateRects();
    }

    setSelection(assets: Asset[] | string[], mode: SelectionMode) {
        let inputIds;
        if (typeof assets[0] === 'string') {
            inputIds = assets;
        } else {
            inputIds = (assets as Asset[]).map((asset) => asset.id);
        }

        const selectedIds = this.selectedAssetIds.slice();

        switch (mode) {
            case SelectionMode.Add:
                const difference = inputIds.filter((x) => !selectedIds.includes(x));
                this.selectedAssetIds = this.selectedAssetIds.concat(difference);
                break;
            case SelectionMode.Replace:
                this.selectedAssetIds = inputIds;
                break;
            case SelectionMode.Clear:
                this.selectedAssetIds.length = 0;
                break;
            case SelectionMode.Remove:
                this.selectedAssetIds = selectedIds.filter((x) => !inputIds.includes(x));
                break;
        }

        this.selection.next(this.selectedAssetIds);
        this.stateService.patchState('selection', this.selectedAssetIds);
    }

    clearSelection() {
        this.setSelection([], SelectionMode.Clear);
        if (this.snackBarRef !== null) {
            this.snackBarRef.dismiss();
        }
    }

    setSelectionMask(rect: Rect) {
        if (this.assetRects.length === 0) {
            this.generateRects();
        }
        this.selectionMask.next(rect);
        const selected = [];
        for (let y = 0; y < this.assetRects.length; y++) {
            for (let x = 0; x < this.assetRects[y].length; x++) {
                if (rect.intersects(this.assetRects[y][x].rect)) {
                    selected.push(this.assetRects[y][x].assetId);
                }
            }
        }

        const mode = this.ctrlDown ? SelectionMode.Add : SelectionMode.Replace;
        this.setSelection(selected, mode);
    }

    setSelectMode(value: boolean) {
        this.selectionMode.next(value);
    }

    private generateRects() {
        const sidenavContent = document.querySelector('mat-sidenav-content');
        const thumbnail = document.querySelector('frog-asset-thumbnail');
        if (sidenavContent === null || thumbnail === null) {
            return;
        }
        this.assetRects.length = 0;

        // FIXME: Is this a good idea to use the dom here?
        // Sample DOM for measurements
        const viewportWidth = sidenavContent.clientWidth;
        const thumbnailSize = thumbnail.clientWidth;
        const columnCount = Math.round(viewportWidth / thumbnailSize);

        let row = 0;
        this.assetRects.push([]);
        this.assetIds.forEach((id, index) => {
            const col = index % columnCount;
            if (col === 0 && this.assetRects[0].length >= columnCount) {
                row += 1;
                this.assetRects.push([]);
            }
            const rect = new Rect(
                col * thumbnailSize + kSidebarWidth,
                row * thumbnailSize + kToolbarHeight,
                thumbnailSize,
                thumbnailSize,
            );
            this.assetRects[row].push({ rect, assetId: id });
        });
    }
}
